import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HesnIcon from '../../assets/logo_icon.png';
import clsx from 'clsx';
import { Footer } from './Footer';
import { NavbarSearch } from '../../features/search/components/NavbarSearch';
import { InstallPrompt } from '../../components/common/InstallPrompt';

export const MainLayout = ({ children, forceSolidNavbar = false, hideNavbar = false, hideFooter = false }: { children: React.ReactNode; forceSolidNavbar?: boolean; hideNavbar?: boolean; hideFooter?: boolean }) => {
    const { t, i18n } = useTranslation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const isRTL = i18n.language.startsWith('ar');

    const toggleLanguage = () => {
        i18n.changeLanguage(isRTL ? 'en' : 'ar');
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems = [
        { key: 'projects', label: t('nav.projects') },
        { key: 'locations', label: t('nav.locations') },
        { key: 'invest', label: t('nav.invest') },
        { key: 'about', label: t('nav.about') }
    ];

    const location = useLocation();
    const isSearchPage = location.pathname.startsWith('/search');

    const showSolidNav = isScrolled || forceSolidNavbar || isSearchPage; // Keep solid nav on search page for consistency? Or maybe just hide the search bar.

    return (
        <div className="min-h-screen bg-background text-text-main font-sans selection:bg-secondary selection:text-white">
            {/* Navigation */}
            {!hideNavbar && (
                <nav className={clsx(
                    "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
                    showSolidNav
                        ? "bg-background/90 backdrop-blur-md border-slate-200 shadow-sm"
                        : "bg-transparent border-transparent py-2"
                )}>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4 sm:gap-8">
                        {/* 1. Logo Section */}
                        <div className="flex-shrink-0">
                            <Link to="/">
                                <img
                                    src={HesnIcon}
                                    alt="HESN Development"
                                    className={clsx(
                                        "h-8 sm:h-12 w-auto object-contain transition-all duration-300",
                                        !showSolidNav && "brightness-0 invert opacity-90"
                                    )}
                                />
                            </Link>
                        </div>

                        {/* 2. Search Bar Section - Centered & Wide */}
                        <div className="flex-grow max-w-2xl px-2 sm:px-4">
                            {!isSearchPage && <NavbarSearch isSolid={showSolidNav} isRTL={isRTL} />}
                        </div>

                        {/* 3. Navigation & Actions Section */}
                        <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
                            {/* Desktop Navigation */}
                            <div className="hidden md:flex items-center gap-6">
                                <div className="hidden md:flex items-center gap-6">
                                    {navItems.map((item) => (
                                        item.key === 'projects' ? (
                                            <Link
                                                key={item.key}
                                                to="/projects"
                                                className={clsx(
                                                    "text-xs lg:text-sm font-bold uppercase tracking-widest transition-colors",
                                                    showSolidNav ? "text-text-muted hover:text-secondary" : "text-slate-300 hover:text-white",
                                                    isRTL && "tracking-normal normal-case"
                                                )}
                                            >
                                                {item.label}
                                            </Link>
                                        ) : (
                                            <a
                                                key={item.key}
                                                href={`/#${item.key}`} // Update to /# to work from other pages
                                                className={clsx(
                                                    "text-xs lg:text-sm font-bold uppercase tracking-widest transition-colors",
                                                    showSolidNav ? "text-text-muted hover:text-secondary" : "text-slate-300 hover:text-white",
                                                    isRTL && "tracking-normal normal-case"
                                                )}
                                            >
                                                {item.label}
                                            </a>
                                        )
                                    ))}
                                </div>
                            </div>

                            {/* Language Toggle - Now grouped with pages */}
                            <button
                                onClick={toggleLanguage}
                                className={clsx(
                                    "px-3 py-1 text-xs font-bold rounded border transition-colors hidden sm:block",
                                    showSolidNav
                                        ? "border-slate-300 text-text-muted hover:text-secondary hover:border-secondary"
                                        : "border-white/50 text-white hover:bg-white hover:text-secondary"
                                )}
                            >
                                {isRTL ? 'EN' : 'عربي'}
                            </button>

                            {/* Mobile Menu Button */}
                            <button
                                className={clsx("md:hidden p-2 transition-colors", showSolidNav ? "text-secondary" : "text-white")}
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-label="Toggle menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileMenuOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden bg-background border-t border-slate-200">
                            <div className="px-4 py-4 space-y-4">
                                {/* Mobile Language Toggle */}
                                <div className="flex justify-end sm:hidden">
                                    <button
                                        onClick={toggleLanguage}
                                        className="px-3 py-1 text-sm font-bold rounded border border-slate-300 text-text-muted hover:text-secondary hover:border-secondary"
                                    >
                                        {isRTL ? 'English' : 'عربي'}
                                    </button>
                                </div>

                                {navItems.map((item) => (
                                    item.key === 'projects' ? (
                                        <Link
                                            key={item.key}
                                            to="/projects"
                                            className={clsx(
                                                "block text-sm font-bold uppercase tracking-widest text-text-muted hover:text-secondary transition-colors py-2",
                                                isRTL && "tracking-normal normal-case"
                                            )}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {item.label}
                                        </Link>
                                    ) : (
                                        <a
                                            key={item.key}
                                            href={`/#${item.key}`}
                                            className={clsx(
                                                "block text-sm font-bold uppercase tracking-widest text-text-muted hover:text-secondary transition-colors py-2",
                                                isRTL && "tracking-normal normal-case"
                                            )}
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {item.label}
                                        </a>
                                    )
                                ))}
                            </div>
                        </div>
                    )}
                </nav>
            )}

            {/* Main Content */}
            <main>
                {children}
            </main>

            {/* Footer */}
            {!hideFooter && <Footer />}

            {/* PWA Install Prompt (Global App Instance) */}
            <InstallPrompt />
        </div>
    );
};
